import { useState, useContext, useEffect } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import { BrowserContext } from "../../services/BrowserContext";

const emptyForm = {
    login: '', password: '',
    plex_server: '', plex_port: '', plex_library: '', plex_token: '',
    is_admin: false,
};

// Dernier segment d'un chemin (nom du dossier), sépareurs / ou \.
const basename = (p) => {
    const t = (p || '').replace(/[/\\]+$/, '');
    const i = Math.max(t.lastIndexOf('/'), t.lastIndexOf('\\'));
    return i >= 0 ? t.slice(i + 1) : t;
};

const UserFormModal = ({ show, handleClose, editingUser, onSaved }) => {
    const { itemsManipulator } = useContext(BrowserContext);
    const [form, setForm] = useState(emptyForm);
    const [folders, setFolders] = useState([{ name: '', path: '', jellyfin: false }]);
    const isEdit = !!editingUser;

    useEffect(() => {
        if (!show) return;
        if (editingUser) {
            setForm({
                login: editingUser.login || '',
                password: '',
                plex_server: editingUser.plex_server || '',
                plex_port: editingUser.plex_port || '',
                plex_library: editingUser.plex_library || '',
                plex_token: editingUser.plex_token || '',
                is_admin: !!Number(editingUser.is_admin),
            });
            setFolders(
                (editingUser.folders && editingUser.folders.length)
                    ? editingUser.folders.map(f => ({ name: f.name || '', path: f.path || '', jellyfin: !!Number(f.jellyfin) }))
                    : [{ name: '', path: '', jellyfin: false }]
            );
        } else {
            setForm(emptyForm);
            setFolders([{ name: '', path: '', jellyfin: false }]);
        }
    }, [show, editingUser]);

    const setField = (key) => (e) => setForm({ ...form, [key]: e.target.value });

    // Le nom suit automatiquement le basename du chemin tant qu'il n'a pas été personnalisé.
    const setFolderPath = (idx) => (e) => {
        const path = e.target.value;
        setFolders(folders.map((f, i) => {
            if (i !== idx) return f;
            const auto = (f.name === '' || f.name === basename(f.path));
            return { ...f, path, name: auto ? basename(path) : f.name };
        }));
    };
    const setFolderName = (idx) => (e) => {
        const name = e.target.value;
        setFolders(folders.map((f, i) => i === idx ? { ...f, name } : f));
    };
    const setFolderJellyfin = (idx) => (e) => {
        const jellyfin = e.target.checked;
        setFolders(folders.map((f, i) => i === idx ? { ...f, jellyfin } : f));
    };
    const addFolder = () => setFolders([...folders, { name: '', path: '', jellyfin: false }]);
    const removeFolder = (idx) => setFolders(folders.filter((_, i) => i !== idx));

    const handleSubmit = (e) => {
        e.preventDefault();
        const cleanFolders = folders
            .filter(f => f.path.trim())
            .map(f => ({ name: f.name.trim() || basename(f.path.trim()), path: f.path.trim(), jellyfin: f.jellyfin ? 1 : 0 }));
        const payload = {
            login: form.login,
            password: form.password,
            folders: JSON.stringify(cleanFolders),
            plex_server: form.plex_server,
            plex_port: form.plex_port,
            plex_library: form.plex_library,
            plex_token: form.plex_token,
            is_admin: form.is_admin ? '1' : '',
        };
        if (isEdit) {
            itemsManipulator.updateUser(payload, onSaved);
        } else {
            itemsManipulator.createUser(payload, onSaved);
        }
    };

    return (
        <Modal show={show} onHide={handleClose} centered>
            <Form onSubmit={handleSubmit}>
                <Modal.Header closeButton>
                    <Modal.Title>{isEdit ? 'Modifier' : 'Nouvel'} utilisateur</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group className="mb-3">
                        <Form.Label>Login</Form.Label>
                        <Form.Control value={form.login} onChange={setField('login')} readOnly={isEdit} required />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>
                            Mot de passe {isEdit && <span className="text-muted small">(laisser vide pour ne pas modifier)</span>}
                        </Form.Label>
                        <Form.Control
                            type="password"
                            value={form.password}
                            onChange={setField('password')}
                            required={!isEdit}
                            autoComplete="new-password"
                        />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Dossiers accessibles</Form.Label>
                        {folders.map((f, idx) => (
                            <div className="mb-2" key={idx}>
                                <div className="d-flex gap-2">
                                    <Form.Control className="flex-grow-1" value={f.path} onChange={setFolderPath(idx)} placeholder="/media/Films" />
                                    <Form.Control style={{ maxWidth: '150px' }} value={f.name} onChange={setFolderName(idx)} placeholder="Nom affiché" />
                                    <Button variant="outline-danger" onClick={() => removeFolder(idx)} title="Retirer" disabled={folders.length === 1}>
                                        <i className="bi bi-trash"></i>
                                    </Button>
                                </div>
                                <Form.Check
                                    type="checkbox"
                                    id={`folder-jellyfin-${idx}`}
                                    className="small text-muted mt-1 ms-1"
                                    label="Bibliothèque Jellyfin (vignettes des films)"
                                    checked={f.jellyfin}
                                    onChange={setFolderJellyfin(idx)}
                                />
                            </div>
                        ))}
                        <Button variant="outline-secondary" size="sm" onClick={addFolder}>
                            <i className="bi bi-plus-lg me-1"></i>Ajouter un dossier
                        </Button>
                    </Form.Group>
                    <hr />
                    <p className="text-muted small mb-2">Configuration Plex (optionnel)</p>
                    <div className="row">
                        <Form.Group className="mb-3 col-md-6">
                            <Form.Label>Serveur Plex</Form.Label>
                            <Form.Control value={form.plex_server} onChange={setField('plex_server')} />
                        </Form.Group>
                        <Form.Group className="mb-3 col-md-6">
                            <Form.Label>Port Plex</Form.Label>
                            <Form.Control value={form.plex_port} onChange={setField('plex_port')} placeholder="32400" />
                        </Form.Group>
                        <Form.Group className="mb-3 col-md-6">
                            <Form.Label>Bibliothèque Plex</Form.Label>
                            <Form.Control value={form.plex_library} onChange={setField('plex_library')} />
                        </Form.Group>
                        <Form.Group className="mb-3 col-md-6">
                            <Form.Label>Token Plex</Form.Label>
                            <Form.Control value={form.plex_token} onChange={setField('plex_token')} />
                        </Form.Group>
                    </div>
                    <Form.Check
                        type="checkbox"
                        id="user-is-admin"
                        label="Administrateur"
                        checked={form.is_admin}
                        onChange={(e) => setForm({ ...form, is_admin: e.target.checked })}
                    />
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleClose}>Annuler</Button>
                    <Button variant="primary" type="submit">{isEdit ? 'Enregistrer' : 'Créer'}</Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

export default UserFormModal;
